/**
 * AppShell
 * The OmniMind frame every screen sits in: the inset gradient app card, the
 * glass sidebar with its navigation groups and user footer, and the top bar.
 */

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Icon, ICONS } from './icons';
import { formatRole } from '../../utils/formatRole';

/**
 * Sidebar navigation.
 * Mirrors the live product's information architecture (Daily Report, Report
 * History, Datasources, Analytics, Settings) and adds the decision surfaces.
 */
interface NavItem {
  path: string;
  label: string;
  icon: keyof typeof ICONS;
  badge?: string;
  badgeTone?: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
  collapsible?: boolean;
}

const NAV: NavGroup[] = [
  {
    label: 'Workspace',
    items: [
      { path: '/', label: 'Ask', icon: 'chat' },
      { path: '/daily-report', label: 'Daily Report', icon: 'sun', badge: 'new', badgeTone: 'gr' },
      { path: '/daily-reports', label: 'Report History', icon: 'hist' },
      { path: '/settings/datasources', label: 'Datasources', icon: 'plug' },
    ],
  },
  {
    label: 'Analytics',
    collapsible: true,
    items: [
      { path: '/analytics/executive', label: 'Executive Dashboard', icon: 'chart' },
      { path: '/analytics/datasource-health', label: 'Data Source Health', icon: 'plug' },
      { path: '/analytics/search', label: 'Search Analytics', icon: 'search' },
      { path: '/analytics/trends', label: 'Trend Analysis', icon: 'chart' },
      { path: '/analytics/anomalies', label: 'Anomaly Detection', icon: 'warn' },
      { path: '/analytics/documents', label: 'Document Intelligence', icon: 'file' },
      { path: '/analytics/adoption', label: 'User Adoption', icon: 'users' },
      { path: '/analytics/reports', label: 'Report Analytics', icon: 'target' },
    ],
  },
  {
    label: 'Decide',
    collapsible: true,
    items: [
      // The real screen, reading /api/core. Everything else in this group is
      // still static content from src/demo. Badging the difference is not
      // decoration: the product documentation's own top risk is a mockup
      // being mistaken for working software in front of a customer, and
      // these five sit side by side in one menu.
      { path: '/consultant', label: 'Consultant', icon: 'bot', badge: 'live', badgeTone: 'gr' },
      { path: '/process', label: 'My answers', icon: 'people', badge: 'live', badgeTone: 'gr' },
      { path: '/metrics', label: 'Metric register', icon: 'book', badge: 'live', badgeTone: 'gr' },
      { path: '/decisions', label: 'Decision Inbox', icon: 'scale', badge: 'live', badgeTone: 'gr' },
      { path: '/agents', label: 'Agents', icon: 'bot', badge: 'demo', badgeTone: 'am' },
      { path: '/trust', label: 'Trust & governance', icon: 'shield', badge: 'demo', badgeTone: 'am' },
      { path: '/log', label: 'Decision log', icon: 'hist', badge: 'demo', badgeTone: 'am' },
      { path: '/metrics/mockup', label: 'Semantic layer (mockup)', icon: 'book', badge: 'demo', badgeTone: 'am' },
      { path: '/decisions/mockup', label: 'Decision Room (mockup)', icon: 'scale', badge: 'demo', badgeTone: 'am' },
    ],
  },
  {
    label: 'Settings',
    collapsible: true,
    items: [
      { path: '/settings/users', label: 'Users', icon: 'users' },
      { path: '/settings/departments', label: 'Departments', icon: 'building' },
      { path: '/settings/category-permissions', label: 'Category Permissions', icon: 'key' },
      { path: '/settings/access-requests', label: 'Access Requests', icon: 'inbox', badge: '2', badgeTone: 'am' },
      { path: '/settings/sso', label: 'Single Sign-On', icon: 'lock' },
    ],
  },
];

interface AppShellProps {
  /** Top bar title */
  title: string;
  /** Top bar sub-line */
  sub?: string;
  /** Right-hand side of the top bar */
  actions?: React.ReactNode;
  /** Extra sidebar content, rendered under the navigation groups */
  sidebarExtra?: React.ReactNode;
  /** Badge shown on the "Data sources" nav item */
  sourceCount?: number;
  /** 'flush' fills the area (chat), 'scroll' pads and scrolls (pages) */
  body?: 'flush' | 'scroll';
  /** Sidebar collapsed state — omit for an always-open sidebar */
  collapsed?: boolean;
  onToggleSidebar?: () => void;
  children: React.ReactNode;
}

function initials(name?: string): string {
  if (!name) return 'U';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
}

export const AppShell: React.FC<AppShellProps> = ({
  title,
  sub,
  actions,
  sidebarExtra,
  sourceCount,
  body = 'scroll',
  collapsed = false,
  onToggleSidebar,
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const at = (path: string) => location.pathname === path;
  const [openGroups, setOpenGroups] = React.useState<string[]>(['Analytics']);
  const roleLabel = formatRole(user?.role);

  return (
    <div className="app">
      <aside className={`side${collapsed ? ' collapsed' : ''}`}>
        <div className="side-hd">
          <span className="omark">
            <i />
          </span>
          <div>
            <h1>OmniMind</h1>
            <div className="tagline">by Elama</div>
          </div>
          <span className="v">AI</span>
        </div>

        <div className="side-scroll custom-scrollbar">
          {NAV.map((group) => {
            const holdsCurrent = group.items.some((i) => at(i.path));
            const open = !group.collapsible || openGroups.includes(group.label) || holdsCurrent;

            return (
              <React.Fragment key={group.label}>
                {group.collapsible ? (
                  <button
                    className={`nav grouphd${open ? ' open' : ''}`}
                    onClick={() =>
                      setOpenGroups(
                        open
                          ? openGroups.filter((g) => g !== group.label)
                          : [...openGroups, group.label]
                      )
                    }
                    aria-expanded={open}
                  >
                    <span className="nav-lbl" style={{ padding: 0 }}>
                      {group.label}
                    </span>
                    <svg
                      className="car"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m9 5 7 7-7 7" />
                    </svg>
                  </button>
                ) : (
                  <div className="nav-lbl">{group.label}</div>
                )}

                <div className={group.collapsible ? `subnav${open ? ' open' : ''}` : undefined}>
                  {group.items.map((item) => (
                    <button
                      key={item.path}
                      className={`nav${group.collapsible ? ' sub' : ''}${at(item.path) ? ' act' : ''}`}
                      onClick={() => navigate(item.path)}
                    >
                      {group.collapsible ? <span className="dotk" /> : <Icon d={ICONS[item.icon]} />}
                      <span>{item.label}</span>
                      {item.path === '/settings/datasources'
                        ? !!sourceCount && <span className="nb">{sourceCount}</span>
                        : item.badge && (
                            <span className={`nb ${item.badgeTone || ''}`}>{item.badge}</span>
                          )}
                    </button>
                  ))}
                </div>
              </React.Fragment>
            );
          })}

          {sidebarExtra}
        </div>

        <div className="side-ft">
          <div className="user">
            <span className="av ink">{initials(user?.fullName)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="n" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.fullName || 'Signed in'}
              </div>
              <div className="r">{roleLabel}</div>
            </div>
            <button className="icb" onClick={logout} title="Sign out">
              <Icon d={ICONS.logout} />
            </button>
          </div>
        </div>
      </aside>

      <div className="main">
        <header className="top">
          {onToggleSidebar && (
            <button className="icb" onClick={onToggleSidebar} title="Toggle sidebar">
              <Icon d={ICONS.menu} />
            </button>
          )}
          <div>
            <h2>{title}</h2>
            {sub && <div className="sub">{sub}</div>}
          </div>
          <div className="tr">{actions}</div>
        </header>

        {body === 'flush' ? (
          children
        ) : (
          <div className="scroll custom-scrollbar">
            <div className="wrap">{children}</div>
          </div>
        )}
      </div>
    </div>
  );
};
